import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Visibility, Delete, DeleteSweep, Add, Search } from '@mui/icons-material';
import { getQuotes, deleteQuote, deleteQuotesBulk, getQuotePrefill } from '../../services/api';
import QuoteForm from '../components/QuoteForm';
import { PRIMARY, SECONDARY, CARD_SHADOW, CARD_SHADOW_HOVER, FONT_HEADING, FONT_BODY, RADIUS, TRANSITION } from '../theme';

const STATUS_META = {
  DRAFT: { label: 'Draft', bg: 'rgba(0,0,0,0.06)', color: '#888' },
  SENT: { label: 'Sent', bg: 'rgba(230, 81, 0, 0.12)', color: '#e65100' },
  ACCEPTED: { label: 'Accepted', bg: 'rgba(46, 125, 50, 0.12)', color: '#2e7d32' },
  DECLINED: { label: 'Declined', bg: 'rgba(211, 47, 47, 0.12)', color: '#d32f2f' },
};

export default function QuotesPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('list');
  const [breadcrumbLabel, setBreadcrumbLabel] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedIds([]);
    try {
      const statusParam = filter === 'all' ? undefined : filter.toUpperCase();
      const res = await getQuotes({ page: page + 1, limit: rowsPerPage, status: statusParam, search: search || undefined });
      setQuotes(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Arriving from an RFQ's "Generate Quote" button
  useEffect(() => {
    const rfqId = location.state?.createFromRfqId;
    if (!rfqId) return;
    (async () => {
      try {
        const res = await getQuotePrefill(rfqId);
        setSelected(res.data);
        setBreadcrumbLabel(`Quote for ${location.state?.rfqName || res.data.customerName}`);
        setView('form');
      } catch (err) {
        setError(err.message || 'Failed to prefill quote from RFQ');
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSelectQuote = (quote) => {
    setSelected(quote);
    setBreadcrumbLabel(null);
    setView('form');
  };

  const handleNewQuote = () => {
    setSelected(null);
    setBreadcrumbLabel(null);
    setView('form');
  };

  const handleBackToList = () => {
    setView('list');
    setSelected(null);
    setBreadcrumbLabel(null);
    fetchData();
  };

  const handleSaved = (savedQuote) => {
    setSelected(savedQuote);
    setBreadcrumbLabel(null);
  };

  // Force back to the list view whenever "Quotes" is clicked in the sidebar, even if a quote is currently open
  useEffect(() => {
    if (!location.state?.resetToList) return;
    handleBackToList();
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleDelete = async (id) => {
    try {
      await deleteQuote(id);
      setDeleteConfirm(null);
      handleBackToList();
    } catch (err) {
      setError(err.message || 'Failed to delete quote');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteQuotesBulk(selectedIds);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete quotes');
    }
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? quotes.map((q) => q.id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

  return (
    <Box>
      {view === 'form' && (
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Link
            component="button"
            underline="hover"
            onClick={handleBackToList}
            sx={{ fontFamily: FONT_BODY, fontWeight: 500, color: PRIMARY, cursor: 'pointer', border: 'none', background: 'none', p: 0, '&:hover': { color: '#5a8499' } }}
          >
            Quotes
          </Link>
          <Typography sx={{ fontFamily: FONT_BODY, color: SECONDARY, fontWeight: 600 }}>
            {breadcrumbLabel || selected?.quoteNumber || 'New Quote'}
          </Typography>
        </Breadcrumbs>
      )}

      {view === 'list' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: FONT_HEADING,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${SECONDARY} 0%, ${PRIMARY} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Quotes
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleNewQuote}
              sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, backgroundColor: PRIMARY, '&:hover': { backgroundColor: '#5a8499' } }}
            >
              New Quote
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <ToggleButtonGroup
              size="small"
              value={filter}
              exclusive
              onChange={(_, v) => { if (v) { setFilter(v); setPage(0); } }}
              sx={{
                gap: 1,
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  '&:not(:first-of-type)': { borderRadius: RADIUS, ml: 0 },
                  '&:first-of-type': { borderRadius: RADIUS },
                },
                '& .MuiToggleButton-root': {
                  fontFamily: FONT_BODY, textTransform: 'none', borderRadius: RADIUS, px: 2.5, py: 0.75, fontSize: '0.875rem', fontWeight: 500,
                  border: '2px solid rgba(108, 152, 173, 0.2)', color: SECONDARY, transition: TRANSITION,
                  '&:hover': { backgroundColor: 'rgba(108, 152, 173, 0.08)', borderColor: PRIMARY },
                  '&.Mui-selected': {
                    backgroundColor: SECONDARY, color: '#fff', borderColor: SECONDARY, boxShadow: '0 2px 8px rgba(36, 39, 42, 0.25)',
                    '&:hover': { backgroundColor: '#3d4348', borderColor: '#3d4348' },
                  },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="draft">Draft</ToggleButton>
              <ToggleButton value="sent">Sent</ToggleButton>
              <ToggleButton value="accepted">Accepted</ToggleButton>
              <ToggleButton value="declined">Declined</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder="Search by name, company, or quote #"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontFamily: FONT_BODY } }}
            />
          </Box>

          {selectedIds.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 2, p: 2, borderRadius: RADIUS,
                background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.04) 100%)',
                border: '2px solid rgba(211, 47, 47, 0.2)', display: 'flex', alignItems: 'center', gap: 2,
              }}
            >
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY }}>
                {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
              </Typography>
              <Button
                variant="contained"
                startIcon={<DeleteSweep />}
                onClick={() => setBulkDeleteConfirm(true)}
                sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
              >
                Delete Selected
              </Button>
              <Button onClick={() => setSelectedIds([])} sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 500, color: '#666' }}>
                Clear Selection
              </Button>
            </Paper>
          )}

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }} onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress sx={{ color: PRIMARY }} />
            </Box>
          ) : quotes.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: RADIUS }}>No quotes found.</Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: RADIUS, border: '2px solid rgba(108, 152, 173, 0.12)', boxShadow: CARD_SHADOW,
                overflow: 'hidden', transition: TRANSITION, '&:hover': { boxShadow: CARD_SHADOW_HOVER },
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: 'rgba(108, 152, 173, 0.05)' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedIds.length > 0 && selectedIds.length < quotes.length}
                          checked={quotes.length > 0 && selectedIds.length === quotes.length}
                          onChange={handleSelectAll}
                          sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY }, '&.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                        />
                      </TableCell>
                      {['Quote #', 'Status', 'Customer', 'Company', 'Total', 'Date', ''].map((h, i) => (
                        <TableCell key={h || 'actions'} align={i === 6 ? 'right' : 'left'} sx={{ fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '0.8rem', color: SECONDARY, letterSpacing: 0.3 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotes.map((q) => (
                      <TableRow
                        key={q.id}
                        hover
                        selected={selectedIds.includes(q.id)}
                        sx={{ cursor: 'pointer', bgcolor: selectedIds.includes(q.id) ? 'rgba(108, 152, 173, 0.12)' : 'transparent', transition: TRANSITION, '&:hover': { bgcolor: 'rgba(108, 152, 173, 0.07)' } }}
                        onClick={() => handleSelectQuote(q)}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedIds.includes(q.id)} onChange={() => handleSelectOne(q.id)} sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />
                        </TableCell>
                        <TableCell sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY }}>{q.quoteNumber}</TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.72rem', borderRadius: '8px', px: 1.25, py: 0.4,
                              bgcolor: STATUS_META[q.status]?.bg, color: STATUS_META[q.status]?.color,
                            }}
                          >
                            {STATUS_META[q.status]?.label || q.status}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: FONT_BODY, color: SECONDARY }}>{q.customerName}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{q.customerCompany || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{formatCurrency(q.total)}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_BODY, color: '#888', fontSize: '0.82rem' }}>{formatDate(q.quoteDate)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectQuote(q); }} sx={{ color: PRIMARY, transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(q); }} sx={{ color: '#d32f2f', transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 15, 25, 50]}
                sx={{ fontFamily: FONT_BODY }}
              />
            </Paper>
          )}
        </>
      )}

      {view === 'form' && (
        <QuoteForm initialQuote={selected} onBack={handleBackToList} onSaved={handleSaved} />
      )}

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} PaperProps={{ sx: { borderRadius: '1rem', boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)' } }}>
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>Delete Quote</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            Are you sure you want to delete quote <strong>{deleteConfirm?.quoteNumber}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 500, color: '#666' }}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirm?.id)} variant="contained" sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} PaperProps={{ sx: { borderRadius: '1rem', boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)' } }}>
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>Delete Multiple Quotes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            Are you sure you want to delete <strong>{selectedIds.length}</strong> quote{selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setBulkDeleteConfirm(false)} sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 500, color: '#666' }}>Cancel</Button>
          <Button onClick={handleBulkDelete} variant="contained" sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT_BODY, fontWeight: 600, backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
